import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEmpty, isNotNil } from '@newfront-insurance/core';
import * as React from 'react';
import type { JSX } from 'react';
import type { ClassNamesConfig, IndicatorsContainerProps, InputActionMeta, MenuPlacement, OptionsOrGroups, GroupBase } from 'react-select';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { IconSelect } from './icons';
import { isGroupedOption } from './lib';
import { fontFamilies, fontSizes, zIndices } from '../../../theme';
import { colors } from '../../../theme/colors';
import { cn } from '../../../v2';
import { IconButton } from '../../icon-button';
import { IconSearch } from '../../icons';
import { Spacing } from '../../spacing';
import { TextLink } from '../../text-link';
import { Tooltip } from '../../tooltip';
import type { GroupedOptions, Option } from '../types';
export interface AdvancedSelectInputProps {
  defaultValue?: string;
  disabled?: boolean;
  isClearable?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  menuPlacement?: MenuPlacement;
  menuPortalTarget?: HTMLElement | null;
  name?: string;
  customFilter?: (option: Option, rawInput: string) => boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (value: string | string[]) => void;
  onInputChange?: (newValue: string, inputActionMeta: InputActionMeta) => void;
  options?: Option[] | GroupedOptions[];
  value?: string | string[] | null;
  placeholder?: string | React.ReactNode;
  icon?: 'select' | 'search';
  id?: string;
  testId?: string;
  inputId?: string;
  hoverColor?: string;
  maxOptionWidth?: number;
  // Passed to React-select. Forces component to only be a dropdown w/o text input
  isSearchable?: boolean;
  classNames?: ClassNamesConfig<Option, true, GroupedOptions>;
  /** Renders compact variant */
  isCompact?: boolean;
  ariaLabel?: string;
}
interface ControlState {
  isFocused: boolean;
  isSelected: boolean;
  data: Option;
}

/**
 * Takes an array of options that may contain option groups and finds
 * the option that has the value that matches the search value.
 * @param options Array of options or grouped options
 * @param value The option value to search for
 */
function findOptionValue(options: OptionsOrGroups<Option, GroupedOptions>, isControlled: boolean, searchValue?: string | null): Option | null | undefined {
  if (!searchValue) return undefined;
  if (isEmpty(options)) return undefined;
  let matched: undefined | null | Option;
  options.forEach((option: Option | GroupedOptions) => {
    if (!isGroupedOption(option)) {
      if (isNotNil(option.value)) {
        if (option.value.toLowerCase() === searchValue.toLowerCase()) {
          matched = option;
        }
      }
    } else if (!matched) {
      matched = findOptionValue(option.options, isControlled, searchValue);
    }
  });
  return matched || (isControlled ? null : undefined);
}

/**
 * Formatter for react-select. This formats the groups of options within
 * the dropdown menu.
 * @param data
 */
function formatGroupLabel(data: GroupedOptions): JSX.Element {
  const groupStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: data.justifyContent || 'space-between',
    textTransform: 'uppercase',
    fontSize: 10,
    fontWeight: 'bold',
    gap: '8px'
  };
  const groupBadgeStyles: React.CSSProperties = {
    display: 'none'
  };
  return <div style={groupStyles}>
      {data.icon && <div>{data.icon}</div>}
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>;
}

/*
  For other React-Select features please see https://react-select.com
  Features not yet implemented:
    - Async: for loading data asynchronously, shows loading indicators
    - Creatable: giving the user the ability to create new options on the fly
    - Fixed Options: works with Multi-select to pre-selected options
*/
export function AdvancedSelectInput(props) {
  const $ = _c(67);
  const {
    defaultValue: t0,
    disabled,
    isClearable,
    isInvalid,
    isLoading,
    isMulti,
    menuPlacement: t1,
    menuPortalTarget: t2,
    name,
    customFilter,
    onBlur,
    onChange,
    onInputChange,
    options: t3,
    value,
    placeholder,
    icon: t4,
    id,
    testId,
    inputId,
    hoverColor,
    isSearchable: t5,
    classNames,
    maxOptionWidth,
    isCompact: t6,
    ariaLabel
  } = props;
  const defaultValue = t0 === undefined ? "" : t0;
  const menuPlacement = t1 === undefined ? "auto" : t1;
  const menuPortalTarget = t2 === undefined ? document.body : t2;
  let t7;
  if ($[0] !== t3) {
    t7 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const options = t7;
  const icon = t4 === undefined ? "select" : t4;
  const isSearchable = t5 === undefined ? true : t5;
  const isCompact = t6 === undefined ? false : t6;
  const isControlled = "value" in props;
  let t8;
  if ($[2] !== defaultValue || $[3] !== isControlled || $[4] !== options) {
    t8 = findOptionValue(options, isControlled, defaultValue);
    $[2] = defaultValue;
    $[3] = isControlled;
    $[4] = options;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const defaultOption = t8;
  let t9;
  if ($[6] !== isControlled || $[7] !== options || $[8] !== value) {
    t9 = value && Array.isArray(value) ? value.map(val => findOptionValue(options, isControlled, val)).filter(_temp) as Option[] : findOptionValue(options, isControlled, value as string);
    $[6] = isControlled;
    $[7] = options;
    $[8] = value;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const optionValue = t9;
  const [isFocused, setFocused] = React.useState(false);
  let t10;
  if ($[10] !== onChange) {
    t10 = function handleChange(option) {
      if (!option) {
        onChange("");
        return;
      }
      if (Array.isArray(option)) {
        onChange(option.map(_temp2));
      } else {
        onChange(option.value);
      }
    };
    $[10] = onChange;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleChange = t10;
  let t11;
  if ($[12] !== maxOptionWidth) {
    t11 = function formatOptionLabel(data, context) {
      const {
        context: optionContext
      } = context;
      const labelMarkup = <div className="flex flex-row items-center" style={{
        marginLeft: optionContext === "menu" ? `${data.spacingLeft}px` : undefined
      }}>{data.icon}<Spacing width={8} /><div className="flex flex-col" style={data.descriptionFlexboxStyles ?? undefined}><span className={cn({
            "overflow-hidden text-ellipsis whitespace-nowrap": maxOptionWidth && optionContext === "value"
          })} style={{
            maxWidth: maxOptionWidth && optionContext === "value" ? `${maxOptionWidth}px` : undefined
          }}>{data.label}</span>{data.description && optionContext === "menu" && <span className="block pt-[2px] text-sm">{data.description}</span>}</div>{data.iconRight}</div>;
      if (optionContext === "value") {
        return <Tooltip message={data.label} maxWidth={400} positionTip="top">{labelMarkup}</Tooltip>;
      }
      return labelMarkup;
    };
    $[12] = maxOptionWidth;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const formatOptionLabel = t11;
  let t12;
  if ($[14] !== isCompact) {
    t12 = styles_1 => ({
      ...styles_1,
      padding: isCompact ? "0 8px" : styles_1.padding
    });
    $[14] = isCompact;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== isCompact || $[17] !== isInvalid) {
    t13 = (_styles, state) => {
      const focusStyles = state.isFocused || state.isSelected ? {
        border: `1px solid ${colors.brand[400]}`
      } : {
        border: `1px solid ${colors.steel[200]}`
      };
      const invalidStyles = isInvalid ? {
        borderColor: colors.fire[600],
        boxShadow: "0 0 0 3px rgba(189, 73, 50, 0.25)"
      } : {};
      return {
        ...focusStyles,
        ...invalidStyles,
        lineHeight: "21px",
        color: colors.steel[150],
        minHeight: isCompact ? undefined : "40px",
        boxSizing: "border-box",
        width: "100%",
        borderRadius: "4px",
        background: "white",
        appearance: "none",
        display: "flex",
        alignItems: "center"
      };
    };
    $[16] = isCompact;
    $[17] = isInvalid;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== hoverColor) {
    t14 = (_provided, state_0) => {
      const focusedColor = hoverColor ?? colors.brand[400];
      const focused = state_0.isFocused ? {
        background: focusedColor,
        color: "white"
      } : {};
      const optionDisabled = state_0.data.isDisabled ? {
        color: colors.steel[200],
        cursor: "default",
        ":hover": {
          background: "none"
        }
      } : {};
      return {
        fontSize: fontSizes.regular.fontSize,
        fontFamily: fontFamilies.body,
        padding: "8px 12px",
        cursor: "pointer",
        color: colors.steel[500],
        background: state_0.isSelected ? colors.steel[100] : "white",
        ":hover": {
          background: focusedColor,
          color: "white"
        },
        ...focused,
        ...optionDisabled,
        ...state_0.data.containerStyles
      };
    };
    $[19] = hoverColor;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t12 || $[22] !== t13 || $[23] !== t14) {
    t15 = {
      dropdownIndicator: _temp3,
      loadingIndicator: _temp4,
      clearIndicator: _temp5,
      container: _temp6,
      valueContainer: t12,
      control: t13,
      option: t14,
      placeholder: _temp7,
      menu: _temp8,
      menuPortal: _temp9,
      multiValue: _temp10,
      multiValueLabel: _temp11,
      multiValueRemove: _temp12,
      noOptionsMessage: _temp13,
      loadingMessage: _temp14,
      input: _temp15
    };
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const selectStyles = t15;
  let t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setFocused(true);
    t17 = () => setFocused(false);
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  const t18 = isMulti || undefined;
  const t19 = `advanced-select-${name}`;
  const t20 = selectStyles as any;
  const t21 = handleChange as any;
  const t22 = optionValue ?? null;
  let t23;
  if ($[27] !== isFocused) {
    t23 = t24 => {
      const {
        clearValue
      } = t24;
      return <StyledIconButtonWrapper onMouseOver={() => {
        if (!isFocused) {
          setFocused(true);
        }
      }}><IconButton type="close" onClick={clearValue} /></StyledIconButtonWrapper>;
    };
    $[27] = isFocused;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== icon || $[30] !== isFocused) {
    t24 = () => <StyledIcon isFocused={isFocused}>{icon === "select" ? <IconSelect testId="asi-icon-select" /> : <IconSearch testId="asi-icon-search" />}</StyledIcon>;
    $[29] = icon;
    $[30] = isFocused;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] !== t23 || $[33] !== t24) {
    t25 = {
      ClearIndicator: t23,
      IndicatorsContainer,
      DropdownIndicator: t24
    };
    $[32] = t23;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  const t26 = !isMulti;
  let t27;
  if ($[35] !== ariaLabel || $[36] !== classNames || $[37] !== customFilter || $[38] !== defaultOption || $[39] !== disabled || $[40] !== formatOptionLabel || $[41] !== inputId || $[42] !== isClearable || $[43] !== isLoading || $[44] !== isSearchable || $[45] !== menuPlacement || $[46] !== menuPortalTarget || $[47] !== onBlur || $[48] !== onInputChange || $[49] !== options || $[50] !== placeholder || $[51] !== t18 || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== t25 || $[57] !== t26 || $[58] !== testId) {
    t27 = <Select classNames={classNames} inputId={inputId} data-testid={testId} defaultValue={defaultOption} classNamePrefix="react-select" filterOption={customFilter} formatGroupLabel={formatGroupLabel} formatOptionLabel={formatOptionLabel} isClearable={isClearable} isSearchable={isSearchable} isDisabled={disabled} isLoading={isLoading} isMulti={t18} menuPortalTarget={menuPortalTarget} menuPlacement={menuPlacement} captureMenuScroll={true} name={t19} options={options} styles={t20} onBlur={onBlur} onChange={t21} onInputChange={onInputChange} placeholder={placeholder} value={t22} aria-label={ariaLabel} components={t25} closeMenuOnSelect={t26} />;
    $[35] = ariaLabel;
    $[36] = classNames;
    $[37] = customFilter;
    $[38] = defaultOption;
    $[39] = disabled;
    $[40] = formatOptionLabel;
    $[41] = inputId;
    $[42] = isClearable;
    $[43] = isLoading;
    $[44] = isSearchable;
    $[45] = menuPlacement;
    $[46] = menuPortalTarget;
    $[47] = onBlur;
    $[48] = onInputChange;
    $[49] = options;
    $[50] = placeholder;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t25;
    $[57] = t26;
    $[58] = testId;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  const t28 = value || "";
  let t29;
  if ($[60] !== id || $[61] !== name || $[62] !== t28) {
    t29 = <input type="hidden" name={name} id={id} value={t28} />;
    $[60] = id;
    $[61] = name;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t27 || $[65] !== t29) {
    t30 = <StyledDiv onFocus={t16} onBlur={t17}>{t27}{t29}</StyledDiv>;
    $[64] = t27;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  return t30;
}
function _temp15(styles_7) {
  return {
    ...styles_7,
    height: "24px",
    marginTop: 2,
    marginBottom: 2
  };
}
function _temp14(styles_6) {
  return {
    ...styles_6,
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.regular.fontSize
  };
}
function _temp13(styles_5) {
  return {
    ...styles_5,
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.regular.fontSize
  };
}
function _temp12() {
  return {
    color: colors.brand[400],
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 24,
    width: 24,
    boxShadow: `1px 0 inset ${colors.glitter[100]}`,
    borderTopRightRadius: 2,
    borderBottomRightRadius: 2,
    marginLeft: 8,
    cursor: "pointer",
    ":hover": {
      background: colors.brand[400],
      color: "white"
    }
  };
}
function _temp11() {
  return {
    color: colors.brand[400],
    padding: "0 0 0 8px"
  };
}
function _temp10() {
  return {
    display: "flex",
    alignItems: "center",
    padding: 0,
    fontSize: 12,
    background: colors.brand[100],
    boxShadow: `0 0 0 1px inset ${colors.glitter[100]}`,
    borderRadius: 2,
    height: 24,
    marginRight: 4,
    marginTop: 2,
    marginBottom: 2
  };
}
function _temp9(styles_4) {
  return {
    ...styles_4,
    zIndex: zIndices.select
  };
}
function _temp8(styles_3) {
  return {
    ...styles_3,
    zIndex: zIndices.select
  };
}
function _temp7(styles_2) {
  return {
    ...styles_2,
    color: colors.steel[300],
    lineHeight: "21px"
  };
}
function _temp6(styles_0) {
  return {
    ...styles_0,
    fontFamily: fontFamilies.body,
    fontSize: fontSizes.regular.fontSize
  };
}
function _temp5() {
  return {
    display: "flex",
    alignItems: "center",
    marginRight: 16,
    ":hover": {
      color: colors.brand[400],
      cursor: "pointer",
      background: colors.steel[100],
      borderRadius: 3
    }
  };
}
function _temp4(styles) {
  return {
    ...styles,
    paddingRight: 16
  };
}
function _temp3() {
  return {
    visibility: "hidden"
  };
}
function _temp2(o) {
  return o.value;
}
function _temp(val_0) {
  return !!val_0;
}
function IndicatorsContainer(props) {
  const $ = _c(13);
  const {
    hasValue,
    selectProps,
    isMulti,
    options,
    setValue
  } = props;
  let t0;
  if ($[0] !== options || $[1] !== setValue) {
    t0 = function handleSelectAll() {
      const optionsToSelect = options.flatMap(_temp17);
      setValue(optionsToSelect as Option[], "select-option");
    };
    $[0] = options;
    $[1] = setValue;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleSelectAll = t0;
  let t1;
  if ($[3] !== handleSelectAll || $[4] !== hasValue || $[5] !== isMulti || $[6] !== selectProps) {
    t1 = isMulti && !hasValue && selectProps.menuIsOpen && <div aria-hidden="true" onMouseDown={_temp18}><TextLink underline={false} underlineOnHover={true} onClick={() => {
        handleSelectAll();
      }}>Select all</TextLink></div>;
    $[3] = handleSelectAll;
    $[4] = hasValue;
    $[5] = isMulti;
    $[6] = selectProps;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props) {
    t2 = React.createElement(components.IndicatorsContainer as React.ComponentType<typeof props>, props);
    $[8] = props;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = <div className="flex flex-row items-center gap-2">{t1}{t2}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp18(e) {
  e.preventDefault();
  e.stopPropagation();
}
function _temp17(option) {
  if (isGroupedOption(option)) {
    return option.options.filter(_temp16);
  }
  return (option as Option).isDisabled ? [] : [option as Option];
}
function _temp16(o) {
  return !o.isDisabled;
}
const StyledIconButtonWrapper = styled.div`
  margin-right: 10px;
`;
const StyledDiv = styled.div`
  position: relative;

  :global(.react-select__control) {
    position: relative;
    z-index: 1;
  }
  :global(.react-select__menu) {
    position: absolute;
    z-index: 2;
  }
  :global(.react-select__single-value) {
    max-width: calc(100% - 80px);
  }
`;
const StyledIcon = styled.div<{
  isFocused: boolean;
}>`
  color: ${({
  isFocused
}) => isFocused ? colors.brand[400] : colors.steel[200]};
  padding-left: 10px;
  padding-right: 10px;
  pointer-events: none;
  z-index: 1;
`;